import { clsx } from 'clsx';
import { useContext } from 'react';
import { backgroundColorClass } from '@quno/patient-journey/src/theme/backgroundColorClass';
import { ModalContext } from '@quno/patient-journey/src/context/ModalContext';
import { useBrand } from '@quno/patient-journey/src/hooks/useBrand';
import Image from 'next/image';
import { getStringMultilocale } from '@quno/onboarding/src/generator/configs/componentMapper';
import {
  defaultLocale,
  localeMapper,
  type FormLocales,
} from '@quno/onboarding/src/constants/locales';
import { useTranslation } from '@quno/patient-journey/src/hooks/useTranslation';
import ExternalFunnelButton from '../shared/ExternalFunnel/ExternalFunnelButton';
import { FunnelCalendarBooking } from '../CalendarBooking/FunnelCalendarBooking';
import styles from './Cta.module.scss';
import type { Package } from '@quno/patient-journey/src/contentful/patient-journey/blocks/Calendar';
import type { Cta as CtaModel } from '@quno/onboarding/src/generator/types/Models';

export type CtaProps = CtaModel & {
  additionalStyles?: {
    backgroundColor?: string;
    fontColor?: string;
  };
  locale?: FormLocales;
};

export const Cta = ({
  attributes,
  additionalStyles,
  ...props
}: CtaProps): JSX.Element | null => {
  const { setModal } = useContext(ModalContext);
  const brand = useBrand();
  const { eventSetup, hideDoctors, product, style, textColor, title, url } =
    attributes ?? {};
  const { locale: pageLocale } = useTranslation();
  const locale = props.locale || localeMapper[pageLocale] || defaultLocale;
  const variant = style?.[defaultLocale];
  const backgroundColor =
    attributes?.backgroundColor?.[defaultLocale] ||
    additionalStyles?.backgroundColor;
  const textColorOverride =
    additionalStyles?.fontColor || textColor?.[defaultLocale];
  const destination = attributes?.destination?.[defaultLocale];
  const text = getStringMultilocale(title, locale);
  const link = getStringMultilocale(url, locale);
  const funnel = attributes?.funnel?.[defaultLocale];

  const iconAttr = attributes?.icon?.[defaultLocale];
  const icon = iconAttr?.url ? (
    <Image
      src={iconAttr?.url}
      width={iconAttr?.width}
      height={iconAttr?.height}
      alt=""
    />
  ) : undefined;

  const buttonClass = clsx(
    styles.button,
    variant === 'secondary' && [styles.buttonSecondary, 'text-underline'],
    variant === 'primary' && backgroundColorClass(backgroundColor),
  );

  if (destination === 'url') {
    return (
      <a
        className={buttonClass}
        href={link!}
        title={text!}
        style={{
          backgroundColor: backgroundColor || undefined,
          color: textColorOverride || undefined,
        }}
      >
        {icon && icon}
        {text}
      </a>
    );
  }

  if (destination === 'funnel' && funnel) {
    return (
      <ExternalFunnelButton
        funnelId={funnel}
        forceFullScreen
        configFromSameDomain
        behavior="fullscreen"
        renderButton={(openFunnel) => {
          return (
            <button
              className={buttonClass}
              style={{
                backgroundColor: backgroundColor || undefined,
                color: textColorOverride || undefined,
              }}
              title={text}
              onClick={() => {
                openFunnel();
              }}
            >
              {icon && icon}
              {text}
            </button>
          );
        }}
        customLogoOverride={brand?.logo}
      />
    );
  }

  if (destination === 'calendar') {
    return (
      <button
        className={buttonClass}
        style={{
          backgroundColor: backgroundColor || undefined,
          color: textColorOverride || undefined,
        }}
        title={text}
        onClick={() => {
          const contentfulPackage = product?.[defaultLocale]
            ? {
                sys: { id: product[defaultLocale] },
              }
            : undefined;

          setModal({
            isOpen: true,
            content: (
              <FunnelCalendarBooking
                eventSetupId={eventSetup?.[defaultLocale] || ''}
                funnelId={funnel || ''}
                contentfulPackage={contentfulPackage as Package}
                hideDoctors={hideDoctors?.[defaultLocale]}
              />
            ),
            showCloseButton: false,
            showPrevIcon: false,
            fullScreen: true,
          });
        }}
      >
        {icon && icon}
        {text}
      </button>
    );
  }

  return null;
};
